import { useTranslation } from 'react-i18next';
import React from 'react';
import { LayoutGrid, List as ListIcon } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Cards ⇄ List view toggle.
 *
 * Value is persisted in localStorage under the `storageKey` prop so each
 * user keeps their preference per-tab. Both modes work at every width —
 * the list tables drop their low-priority columns on narrow screens and
 * let their contents wrap, rather than forcing a sideways scroll.
 */
export default function ViewToggle({ storageKey, value, onChange }) {
  const { t } = useTranslation();
  const set = (next) => {
    onChange(next);
    try {
      if (storageKey) localStorage.setItem(storageKey, next);
    } catch { /* ignore quota / private mode */ }
  };

  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden shrink-0" data-testid="view-toggle">
      <Button
        type="button"
        variant={value === 'cards' ? 'default' : 'ghost'}
        size="sm"
        className="rounded-none h-9 px-3"
        onClick={() => set('cards')}
        title={t('auto.misc2.cardsView')}
        data-testid="view-toggle-cards"
      >
        <LayoutGrid className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={value === 'list' ? 'default' : 'ghost'}
        size="sm"
        className="rounded-none h-9 px-3"
        onClick={() => set('list')}
        title={t('auto.misc2.listView')}
        data-testid="view-toggle-list"
      >
        <ListIcon className="w-4 h-4" />
      </Button>
    </div>
  );
}

/**
 * Read the stored view preference (falls back to `defaultView`).
 * Small helper so callers can seed useState synchronously.
 */
export function readStoredView(storageKey, defaultView = 'cards') {
  try {
    const v = localStorage.getItem(storageKey);
    return v === 'list' || v === 'cards' ? v : defaultView;
  } catch {
    return defaultView;
  }
}
